// src/pages/StoresPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Store, Plus, Edit2, Phone, MapPin,
  CheckCircle, Building2, Percent
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { storeApi } from '../api';

const StoresPage = () => {
  const { language } = useApp();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const emptyForm = { name: '', name_en: '', address: '', phone: '', status: 'active', commission_rate: '5' };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadStores(); }, []);

  const loadStores = async () => {
    setLoading(true);
    try {
      const res = await storeApi.getList();
      if (res.success) setStores(res.data);
    } catch (err) {
      console.error('加载门店失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingStore(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (store) => {
    setEditingStore(store);
    setForm({
      name: store.name || '',
      name_en: store.name_en || '',
      address: store.address || '',
      phone: store.phone || '',
      status: store.status || 'active',
      commission_rate: store.commission_rate ? (parseFloat(store.commission_rate) * 100).toFixed(1) : '5'
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('门店名称不能为空'); return; }
    const rate = parseFloat(form.commission_rate);
    if (isNaN(rate) || rate < 0 || rate > 100) { setError('佣金比例需在 0-100 之间'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        commission_rate: (rate / 100).toFixed(4)  // 转为小数存储，如 5% → 0.0500
      };
      let res;
      if (editingStore) {
        res = await storeApi.update(editingStore.id, payload);
      } else {
        res = await storeApi.create({ ...payload, type: 'branch' });
      }
      if (res.success) { setShowModal(false); loadStores(); }
      else setError(res.message || '操作失败');
    } catch (err) {
      setError(err.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const headquarters = stores.find(s => s.type === 'headquarters');
  const branches = stores.filter(s => s.type === 'branch');
  const isBranch = editingStore?.type === 'branch' || !editingStore;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">门店管理</h2>
          <p className="text-gray-600 mt-1">管理总店与所有分店信息</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>新增分店</span>
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">门店总数</p>
              <p className="text-3xl font-bold mt-2">{stores.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Building2 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">分店数量</p>
              <p className="text-3xl font-bold mt-2">{branches.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Store className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">营业中</p>
              <p className="text-3xl font-bold mt-2">{stores.filter(s => s.status === 'active').length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 总店 */}
      {headquarters && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            <span>总店</span>
          </h3>
          <div className="bg-white rounded-xl shadow border-l-4 border-blue-600 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-lg font-bold">{headquarters.name}</h4>
                    <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">总店</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${headquarters.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                      {headquarters.status === 'active' ? '营业中' : '已关闭'}
                    </span>
                  </div>
                  {headquarters.name_en && <p className="text-gray-500 text-sm mt-0.5">{headquarters.name_en}</p>}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    {headquarters.address && (
                      <span className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" /><span>{headquarters.address}</span>
                      </span>
                    )}
                    {headquarters.phone && (
                      <span className="flex items-center space-x-1">
                        <Phone className="w-4 h-4" /><span>{headquarters.phone}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => openEdit(headquarters)}
                className="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50">
                <Edit2 className="w-4 h-4" /><span className="text-sm">编辑</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 分店列表 */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center space-x-2">
          <Store className="w-5 h-5 text-purple-600" />
          <span>分店列表</span>
          <span className="bg-gray-100 text-gray-600 text-sm px-2 py-0.5 rounded-full">{branches.length}</span>
        </h3>

        {branches.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center">
            <Store className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">暂无分店</p>
            <button onClick={openCreate} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
              新增分店
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {branches.map(store => (
              <div key={store.id} className="bg-white rounded-xl shadow p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="bg-purple-100 p-3 rounded-full">
                      <Store className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-bold">{store.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${store.status === 'active' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                          {store.status === 'active' ? '营业中' : '已关闭'}
                        </span>
                      </div>
                      {store.name_en && <p className="text-gray-500 text-sm mt-0.5">{store.name_en}</p>}
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        {store.address && (
                          <p className="flex items-center space-x-1">
                            <MapPin className="w-3.5 h-3.5" /><span>{store.address}</span>
                          </p>
                        )}
                        {store.phone && (
                          <p className="flex items-center space-x-1">
                            <Phone className="w-3.5 h-3.5" /><span>{store.phone}</span>
                          </p>
                        )}
                        {/* 佣金比例显示 */}
                        <p className="flex items-center space-x-1 text-orange-600 font-medium">
                          <Percent className="w-3.5 h-3.5" />
                          <span>管理费：{store.commission_rate ? (parseFloat(store.commission_rate) * 100).toFixed(1) : '5.0'}%</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => openEdit(store)}
                    className="flex items-center space-x-1 text-gray-500 hover:text-blue-600 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-50">
                    <Edit2 className="w-4 h-4" /><span className="text-sm">编辑</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 新增/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-bold">{editingStore ? '编辑门店' : '新增分店'}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">门店名称 <span className="text-red-500">*</span></label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="例：柑仔店北区分店" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">英文名称</label>
                <input type="text" value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="例：North Branch" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">地址</label>
                <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="门店地址" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">联系电话</label>
                <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="联系电话" />
              </div>

              {/* 佣金比例 — 仅分店显示 */}
              {isBranch && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    管理费比例（%）
                    <span className="text-gray-400 text-xs ml-1">— 每笔订单总店收取的佣金百分比</span>
                  </label>
                  <div className="relative">
                    <input type="number" min="0" max="100" step="0.1"
                      value={form.commission_rate}
                      onChange={e => setForm({ ...form, commission_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                      placeholder="5.0" />
                    <span className="absolute right-3 top-2.5 text-gray-400">%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    例：输入 5 表示总店收取订单金额的 5%，分店得剩余 95% + 配送费
                  </p>
                </div>
              )}

              {editingStore && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="active">营业中</option>
                    <option value="inactive">已关闭</option>
                  </select>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
              )}
            </div>
            <div className="p-6 border-t flex space-x-3">
              <button onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                取消
              </button>
              <button onClick={handleSave} disabled={saving}
                className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg ${saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}>
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoresPage;